import type { AlertRuleListQuery } from '@chukua/common';
import { Button, Switch, Tag, type TableColumnsType } from 'antd';
import type { ListQueryControls } from '../../../shared/hooks/use-list-query';
import type { AlertRuleView } from '../alerts.api';
import { ALERT_SCOPE_LABELS, ALERT_SEVERITY_LABELS, ALERT_TYPE_LABELS, SEVERITY_TAG_COLOURS, toColumnFilters } from '../policies/alert-labels';
import { describeScope, describeThresholds } from '../policies/alert-rule-form';

interface AlertRuleColumnsOptions {
  list: ListQueryControls<AlertRuleListQuery>;
  onEdit: (rule: AlertRuleView) => void;
  onToggle: (rule: AlertRuleView) => void;
}

export function alertRuleColumns({ list, onEdit, onToggle }: AlertRuleColumnsOptions): TableColumnsType<AlertRuleView> {
  return [
    { title: 'Name', key: 'name', dataIndex: 'name', sorter: true, sortOrder: list.sortOrderFor('name') },
    {
      title: 'Type',
      key: 'type',
      sorter: true,
      sortOrder: list.sortOrderFor('type'),
      filters: toColumnFilters(ALERT_TYPE_LABELS),
      filteredValue: list.filteredValueFor('type'),
      render: (_, rule) => ALERT_TYPE_LABELS[rule.type],
    },
    { title: 'Fires when', key: 'thresholds', render: (_, rule) => describeThresholds(rule) },
    {
      title: 'Applies to',
      key: 'scopeKind',
      filters: toColumnFilters(ALERT_SCOPE_LABELS),
      filteredValue: list.filteredValueFor('scopeKind'),
      render: (_, rule) => describeScope(rule),
    },
    {
      title: 'Severity',
      key: 'severity',
      sorter: true,
      sortOrder: list.sortOrderFor('severity'),
      filters: toColumnFilters(ALERT_SEVERITY_LABELS),
      filteredValue: list.filteredValueFor('severity'),
      render: (_, rule) => <Tag color={SEVERITY_TAG_COLOURS[rule.severity]}>{ALERT_SEVERITY_LABELS[rule.severity]}</Tag>,
    },
    {
      title: 'On',
      key: 'enabled',
      filters: [
        { value: 'true', text: 'On' },
        { value: 'false', text: 'Off' },
      ],
      filteredValue: list.filteredValueFor('enabled'),
      render: (_, rule) => <Switch checked={rule.isEnabled} aria-label={`${rule.name} on`} onChange={() => onToggle(rule)} />,
    },
    {
      title: '',
      key: 'actions',
      render: (_, rule) => (
        <Button size="small" onClick={() => onEdit(rule)}>
          Edit
        </Button>
      ),
    },
  ];
}
